import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Loader2, Bus, UtensilsCrossed } from 'lucide-react';
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AttendancePage = () => {
  const navigate = useNavigate();

  const path = window.location.pathname;

  const token = path.startsWith("/lkart/admin")
    ? localStorage.getItem("admin_auth_token")
    : path.startsWith("/lkart/superadmin")
      ? localStorage.getItem("superadmintoken")
      : path.startsWith("/lkart/dashboard")
        ? localStorage.getItem("worker_auth_token")
        : null;

  const currentDate = new Date();

  // Function to get current month-year in "M-YYYY" format
  const getCurrentMonthYear = () => {
    return `${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
  };

  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentMonthYear());
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalWorkingHours, setTotalWorkingHours] = useState('0');

  // Guards against overlapping requests and stale responses landing out of order
  const fetchingRef = useRef(false);
  const requestIdRef = useRef(0);

  // Sentinel element sitting just after the table — when it enters the
  // viewport (works with normal page/window scroll, so it behaves the same
  // on mobile touch-scroll as it does on desktop) we load the next page.
  const sentinelRef = useRef(null);

  const generateMonthYearOptions = () => {
    const options = [{ value: 'all', label: 'All Periods' }];
    const startYear = 2025;

    for (let year = startYear; year <= currentDate.getFullYear(); year++) {
      for (let month = 0; month < 12; month++) {
        if (year === currentDate.getFullYear() && month > currentDate.getMonth()) break;
        options.push({
          value: `${month + 1}-${year}`,
          label: `${new Date(year, month).toLocaleString('default', { month: 'short' })}, ${year}`
        });
      }
    }
    return options;
  };

  const monthYearOptions = generateMonthYearOptions();

  const formatDateTime = (isoString) => {
    if (!isoString) return { date: '', time: '' };

    const dateObj = new Date(isoString);

    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    const date = `${day} ${month} ${year}`;

    let hours = dateObj.getUTCHours();
    const minutes = dateObj.getUTCMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const time = `${hours}:${minutes} ${ampm}`;

    return { date, time };
  };

  // ── Fetch a single page and append/replace ─────────────────────────────
  const fetchPage = useCallback(async (pageToFetch, reset) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);

    const thisRequestId = ++requestIdRef.current;

    try {
      let params = { page: pageToFetch };

      if (selectedPeriod !== 'all') {
        const [month, year] = selectedPeriod.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1).toLocaleDateString('en-CA');
        const endDate = new Date(year, month, 0).toLocaleDateString('en-CA');
        params = { ...params, startDate, endDate };
      }

      if (path.startsWith('/lkart/admin/worker-attendance/')) {
        const workerId = path.split('/').pop();
        if (workerId) {
          params = { ...params, workerId };
        }
      }

      const response = await axios.get(`${BASE_URL}/lenskart/history`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      // If a newer request has started since this one was fired, drop this result
      if (thisRequestId !== requestIdRef.current) return;

      const result = response.data;
      const newRecords = result.data.attendance || [];

      const transformedRecords = newRecords.map(record => {
        const entryDateTime = formatDateTime(record.entryTime);
        const exitDateTime = formatDateTime(record.exitTime);

        return {
          id: record.id,
          entryDate: entryDateTime.date,
          entryTime: entryDateTime.time,
          exitDate: exitDateTime.date,
          exitTime: exitDateTime.time,
          workingHours: record.workingHours ? record.workingHours.toFixed(2) : '0.00',
          busEntry: record.benefits?.busEntry || false,
          busExit: record.benefits?.busExit || false,
          haveFood: record.benefits?.hadFood || false,
          company: record.company || 'N/A',
          companyLocation: record.companyLocation || 'N/A',
          department: record.department || '',
        };
      });

      setAttendanceData(prev => reset ? transformedRecords : [...prev, ...transformedRecords]);
      setTotalWorkingHours(result.data.summary?.totalWorkingHours?.toFixed(2) || '0');

      const totalPages = result.data.pagination?.totalPages || 1;
      const hasNextPage =
        result.data.pagination?.hasNextPage ?? pageToFetch < totalPages;
      setHasMore(hasNextPage);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      if (thisRequestId === requestIdRef.current) setHasMore(false);
    } finally {
      if (thisRequestId === requestIdRef.current) setLoading(false);
      fetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, token, path]);

  // ── Reset and fetch page 1 whenever the period filter changes ──────────
  useEffect(() => {
    setAttendanceData([]);
    setHasMore(true);
    setPage(1);
    fetchPage(1, true);
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  // ── Fetch subsequent pages whenever `page` advances past 1 ─────────────
  useEffect(() => {
    if (page === 1) return; // page 1 is handled by the period-change effect
    fetchPage(page, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ── Infinite scroll via IntersectionObserver on a sentinel element ─────
  // This tracks the *page's* natural scroll (works identically for mouse
  // wheel, trackpad, and mobile touch-scroll) instead of listening on a
  // fixed-height inner div, which is what broke on mobile.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !fetchingRef.current) {
          setPage(prev => prev + 1);
        }
      },
      {
        root: null, // observe against the viewport, not a nested scroll container
        rootMargin: '200px', // start loading a bit before the sentinel is on-screen
        threshold: 0,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, attendanceData.length]);

  return (
    <div className="min-h-screen bg-gray-100 p-0 pt-20 pb-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-4">
        {/* ← Back button + title */}
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              position: "absolute",
              left: "0",
              background: "none",
              border: "none",
              fontSize: "28px",
              color: "#222",
              cursor: "pointer",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ‹
          </button>
          <h2 className="text-2xl font-bold text-center">Attendance History</h2>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <div className="text-sm text-gray-800 font-medium">
            Total Working Hours: {totalWorkingHours} Hr
          </div>

          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm w-full sm:w-auto"
          >
            {monthYearOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* No inner scroll container — the page itself scrolls, which is what
            makes this behave correctly on mobile touch-scroll. */}
        <div className="w-full">
          {loading && attendanceData.length === 0 ? (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse min-w-[600px]">
                <thead className="sticky top-0 bg-white shadow-md z-10">
                  <tr className="bg-gray-100">
                    <th className="p-2 sm:p-3 text-center text-xs sm:text-sm font-semibold whitespace-nowrap">Company</th>
                    <th className="p-2 sm:p-3 text-center text-xs sm:text-sm font-semibold whitespace-nowrap">Location</th>
                    <th className="p-2 sm:p-3 text-center text-xs sm:text-sm font-semibold whitespace-nowrap">Department</th>
                    <th className="p-2 sm:p-3 text-center text-xs sm:text-sm font-semibold whitespace-nowrap">Entry Date & Time</th>
                    <th className="p-2 sm:p-3 text-center text-xs sm:text-sm font-semibold whitespace-nowrap">Exit Date & Time</th>
                    <th className="p-2 sm:p-3 text-center text-xs sm:text-sm font-semibold whitespace-nowrap">Working Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceData.length > 0 ? (
                    attendanceData.map(record => (
                      <tr key={record.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 sm:p-3 text-center">
                          <span className="text-xs sm:text-sm">{record.company.toUpperCase()}</span>
                        </td>
                        <td className="p-2 sm:p-3 text-center">
                          <span className="text-xs sm:text-sm">{record.companyLocation}</span>
                        </td>
                        <td className="p-2 sm:p-3 text-center">
                          <span className="text-xs sm:text-sm">{record.department.toUpperCase()}</span>
                        </td>
                        <td className="p-2 sm:p-3 text-center">
                          <div className="flex flex-col items-center justify-center space-y-1">
                            <div className="flex items-center justify-center">
                              <span className="text-xs sm:text-sm">{record.entryDate}</span>
                              {record.busEntry && (
                                <Bus className="ml-1 h-3 w-3 sm:h-4 sm:w-4 text-[#3D5A80]" title="Used bus service for entry" />
                              )}
                            </div>
                            <span className="text-xs text-gray-600">{record.entryTime}</span>
                          </div>
                        </td>
                        <td className="p-2 sm:p-3 text-center">
                          {record.exitTime ? (
                            <div className="flex flex-col items-center justify-center space-y-1">
                              <div className="flex items-center justify-center">
                                <span className="text-xs sm:text-sm">{record.exitDate}</span>
                                {record.busExit && (
                                  <Bus className="ml-1 h-3 w-3 sm:h-4 sm:w-4 text-[#3D5A80]" title="Used bus service for exit" />
                                )}
                              </div>
                              <div className="flex items-center justify-center">
                                <span className="text-xs text-gray-600">{record.exitTime}</span>
                                {record.haveFood && (
                                  <UtensilsCrossed className="ml-1 h-3 w-3 sm:h-4 sm:w-4 text-[#F4A261]" title="Availed food service" />
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-red-500 font-medium text-xs sm:text-sm">No exit</span>
                          )}
                        </td>
                        <td className="p-2 sm:p-3 text-center">
                          <span className="text-xs sm:text-sm font-medium">{record.workingHours} Hr</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center p-4 text-gray-500 text-sm">No records found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Sentinel: an empty element the IntersectionObserver watches.
              Placing it right after the table means "user is near the
              bottom of the content" fires page load reliably on mobile. */}
          <div ref={sentinelRef} style={{ height: 1 }} />

          {loading && attendanceData.length > 0 && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
          {!loading && !hasMore && attendanceData.length > 0 && (
            <div className="text-center text-xs text-gray-400 py-4">
              No more records
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendancePage;